<template>
    <div class="campaign-template-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <campaign-template-widget-strategy-step 
                        v-if="currentStep === steps.strategy" 
                        ref="strategyStep"
                        @nextStep="goToStep('campaignSettings')"/>

                    <campaign-template-widget-settings-step
                        v-if="currentStep === steps.campaignSettings" 
                        ref="settingsStep"
                        @closeWidget="closeWidget($event)"
                        @nextStep="goToNextStepAfterSettings()" />

                    <campaign-template-widget-ab-distribution-step
                        v-if="currentStep === steps.abDistribution" 
                        ref="abDistribution"
                        @nextStep="goToStep('schedule')" />
                        
                    <campaign-template-widget-review-step
                        v-if="currentStep === steps.review" 
                        ref="reviewStep"
                        @nextStep="closeWidget()"
                        @goToStep="goToStep($event)"
                        @closeWidget="closeWidget()" />

                    <campaign-template-widget-template-schedule-step
                        v-if="currentStep === steps.schedule" 
                        ref="scheduleStep"
                        @nextStep="goToStep('review')"
                        @campaign:edit="goToStep($event)"/>

                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div v-if="!isLoading" class="action-container">
                    <app-button v-if="currentStep != steps['strategy']" class="back-btn" @click="goToPreviousStep">
                        Back
                    </app-button>
                    <app-button class="save-btn" @click="currentStep.action">
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const CampaignTemplateWidgetStrategyStep = () => import('@/components/sliding-widgets/campaign-template-widget/campaign-template-widget-strategy-step');
const CampaignTemplateWidgetSettingsStep = () => import('@/components/sliding-widgets/campaign-template-widget/campaign-template-widget-settings-step');
const CampaignTemplateWidgetAbDistributionStep = () => import('@/components/sliding-widgets/campaign-template-widget/campaign-template-widget-ab-distribution-step');
const CampaignTemplateWidgetReviewStep = () => import('@/components/sliding-widgets/campaign-template-widget/campaign-template-widget-review-step');
const CampaignTemplateWidgetScheduleStep = () => import('@/components/sliding-widgets/campaign-template-widget/campaign-template-widget-schedule-step');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'campaign-template-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'campaign-template-widget-strategy-step': CampaignTemplateWidgetStrategyStep,
        'campaign-template-widget-settings-step': CampaignTemplateWidgetSettingsStep,
        'campaign-template-widget-ab-distribution-step':CampaignTemplateWidgetAbDistributionStep,
        'campaign-template-widget-review-step': CampaignTemplateWidgetReviewStep,
        'campaign-template-widget-template-schedule-step': CampaignTemplateWidgetScheduleStep,
    },

    provide() {
        return {
            'campaignTemplate': this.campaignTemplate        
        };
    },

    props: {
        options: {
            type: Object,
            default: null
        },

        widget: {
            type: Object,
            required: true
        }
    },


    data() {
        return {
            isLoading: true,

            steps: {
                strategy: { title: 'Campaign Type', buttonText: 'Next', loading: false, action: () => {this.$refs.strategyStep.next();} },
                campaignSettings: { title: 'Settings', buttonText: 'Save & Continue', loading: false, action: () => {this.$refs.settingsStep.next();} },
                abDistribution: { title: 'A/B Split', buttonText: 'Save & Continue', loading: false, action: () => {this.$refs.abDistribution.patchCampaign();} },
                review: { title: 'Review', buttonText: 'Save & Close', loading: false, action: () => {this.$refs.reviewStep.next();} },
                schedule: { 
                    title: 'Set time', 
                    buttonText: 'Save & Continue', 
                    loading: false, 
                    action: () => {this.$refs.scheduleStep.patch();}}
            },

            currentStep: null,

            campaignTemplate: {
                id: this.widget.campaignTemplateId,
                day: this.widget.day,
                marketingPlanTemplateId: this.widget.marketingPlanTemplateId,
                clientId: this.widget.clientId || this.$store.state.context.client.id,
                agencyId: this.widget.agencyId || this.$store.state.context.agency.id,
                testType: null,
                isGraphical: true,
                hasAbTest: false,
                name: null,
                subjectA: null,
                subjectB: null,
                testMetric: null,
                testedContactsPercentage: 0,
                duration: 0,
                unit: null,
                previewImageUrl: null,
                sendTime: null,
                configurations: {
                    configurationA: null,
                    configurationB: null
                }
            }
        }
    },

    created() {
        if (this.campaignTemplate.id){
            this.getCampaignTemplate() 
                .then(() => {
                    var missingSettings = !this.campaignTemplate.name || !this.campaignTemplate.subjectA || !this.campaignTemplate.configurations.configurationA;
                    var missingABSubjectSettings = this.campaignTemplate.hasAbTest && this.campaignTemplate.testType == 'Subject' &&  !this.campaignTemplate.subjectB;
                    var missingABContentSettings = this.campaignTemplate.hasAbTest && this.campaignTemplate.testType == 'Content' && !this.campaignTemplate.configurations.configurationB;
                    var missingABSettings = this.campaignTemplate.hasAbTest && (!this.campaignTemplate.testedContactsPercentage || !this.campaignTemplate.duration || !this.campaignTemplate.testMetric);
                    var missingScheduleSettings = !this.campaignTemplate.scheduledSendDate;
                    var readyForReview = this.campaignTemplate.name && this.campaignTemplate.subjectA && this.campaignTemplate.configurations.configurationA && this.campaignTemplate.configurations.sendTime ;
                    var readyForReviewSubjectTesting = this.campaignTemplate.testType == 'Subject' && readyForReview && this.campaignTemplate.subjectB && !missingABSettings && this.campaignTemplate.duration && this.campaignTemplate.unit;
                    var readyForReviewContentTesting = this.campaignTemplate.testType == 'Content' && readyForReview && this.campaignTemplate.configurations.configurationB && this.campaignTemplate.duration && this.campaignTemplate.unit;

                    if (readyForReviewSubjectTesting || readyForReviewContentTesting || (readyForReview && !this.campaignTemplate.hasAbTest)) {
                        this.goToStep('review');
                    } 
                    else if (missingABSubjectSettings || missingABContentSettings || missingSettings) {
                        this.goToStep('campaignSettings');
                    }
                    else if (missingABSettings) {
                        this.goToStep('abDistribution')
                    } 
                    else if (!this.campaignTemplate.sendTime) {
                        this.goToStep('schedule');
                    }
                    else if (missingScheduleSettings){
                        this.goToStep('review')
                    }
                    else {
                        this.goToStep('strategy');
                    }

                    this.isLoading = false;
                });
        }
        else {
            this.goToStep('strategy');
            this.isLoading = false;
        } 
    },

    methods: {
        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        goToPreviousStep() {
            if(this.currentStep === this.steps.campaignSettings) {
                this.goToStep('strategy');
            } 
            else if (this.currentStep === this.steps.abDistribution) {
                this.goToStep('campaignSettings');
            } 
            else if (this.currentStep === this.steps.schedule) {
                if (!this.campaignTemplate.hasAbTest) {
                    this.goToStep('campaignSettings');
                } 
                else {
                    this.goToStep('abDistribution');
                }
            } 
            else if (this.currentStep === this.steps.review) {
                this.goToStep('schedule');
            }
        },

        goToNextStepAfterSettings() {
            if (this.campaignTemplate.hasAbTest) {
                this.goToStep('abDistribution');
            }
            else {
                this.goToStep('schedule');
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getCampaignTemplate() {
            return new Promise((resolve, reject) => {
                var select = `$select=ClientId,IsGraphical,HasAbTest,TestType,Name,SubjectA,SubjectB,TestDurationAmount,TestPercent,TestMetric,TestDurationType,SendTime,ConfigurationA,ConfigurationB,PreviewImageUrl`
                this.$api
                    .get(`/service/CampaignTemplates/?$filter=Id eq ${ this.campaignTemplate.id }&${ select }`)
                    .then(response => {
                        var responseValue = response.data.value[0];

                        this.campaignTemplate.clientId = responseValue.ClientId;
                        this.campaignTemplate.isGraphical = responseValue.IsGraphical;
                        this.campaignTemplate.hasAbTest = responseValue.HasAbTest;
                        this.campaignTemplate.testType = responseValue.TestType;
                        this.campaignTemplate.name = responseValue.Name;
                        this.campaignTemplate.subjectA = responseValue.SubjectA;
                        this.campaignTemplate.subjectB = responseValue.SubjectB;
                        this.campaignTemplate.duration = responseValue.TestDurationAmount;
                        this.campaignTemplate.testedContactsPercentage = responseValue.TestPercent;
                        this.campaignTemplate.testMetric = responseValue.TestMetric;
                        this.campaignTemplate.unit = responseValue.TestDurationType;
                        this.campaignTemplate.sendTime = responseValue.SendTime ? this.$moment(responseValue.SendTime).local().format('hh:mm A') : null;
                        this.campaignTemplate.configurations.configurationA = JSON.parse(responseValue.ConfigurationA);
                        this.campaignTemplate.configurations.configurationB = JSON.parse(responseValue.ConfigurationB);
                        this.campaignTemplate.previewImageUrl = responseValue.PreviewImageUrl;

                        if (this.campaignTemplate.sendTime != null && this.campaignTemplate.sendTime.charAt(0) === '0') {
                            this.campaignTemplate.sendTime = this.campaignTemplate.sendTime.substring(1)
                        }
                        resolve();
                    })
                    .catch(reject);  
            });
        },
        
    },
}
</script>

<style>


.campaign-template-widget .input-label {
    font-weight: 600;
    display: flex;
    align-items: center;
}

.campaign-template-widget .action-container {
    display: flex;
}

.campaign-template-widget .action-container .save-btn {
    width: 128px;
}

.campaign-template-widget .action-container .back-btn {
    width: 128px;
    background: transparent;
    color: #18AEFF;
    margin-right: 10px;
}

.campaign-template-widget .action-container .back-btn:hover {
    background: #E1E0E5;
}

.campaign-template-widget .cancel-btn {
    background: transparent;
    color: #877D88;
    border: none;
}

.campaign-template-widget .cancel-btn:hover {
    background: #E9E9EF;
}

.campaign-template-widget .error {
    color: #ff0000;
    font-size: 14px;
}

.campaign-template-widget .app-input label {
    display: none;
}

.campaign-template-widget .error-message {
    display: none;
}

.campaign-template-widget .widget-contents-container .content-create {
    padding: 0;
}

.campaign-template-widget .content-create .app-text-editor-wrapper,
.campaign-template-widget .content-create .app-text-editor,
.campaign-template-widget .content-create .widget-container {
    height: 100%;
}

.campaign-template-widget .content-create .widget-container .controls {
    display: none;
}

</style>